import React, { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import Portal from '../../atoms/Portal/Portal';
import { DivProps, SpanProps } from '../../../types/HtmlProps';
import { CollapsibleProps } from '../../../hooks/types/useCollapsibleOverlay';
import { CollapsibleModifier } from './CollapsibleOverlay.modifier';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { appendClassNames } from '../../../lib/bemUtils/classname';
import extendClassName from '../../../lib/bemUtils/bem';

type CollapsibleContentProps = DivProps &
  CollapsibleProps<HTMLDivElement> & {
    modifier?: Modifier<CollapsibleModifier>;
    ref?: React.RefObject<HTMLDivElement | null>;
  };

/**
 * Sometimes it is helpful to have children in DOM, especially on form elements with data sent to the backend.
 * See MultiSelect.
 */
const returnChildrenOrNothing = (isOpen: boolean, returnChildrenWhenClosed: boolean, children: ReactNode) => {
  if (isOpen || returnChildrenWhenClosed) {
    return children;
  }

  return null;
};

/**
 * Base CollapsibleContent component capable of being rendered as `htmlTag`
 */
export const CollapsibleContent = ({
  children,
  returnChildrenWhenClosed = false,
  isOpen,
  className,
  modifier,
  ref,
  ...props
}: PropsWithChildren<CollapsibleContentProps>) => {
  return (
    <div {...props} className={appendClassNames(className, extendClassName('collapsible-overlay', modifier))} ref={ref}>
      {returnChildrenOrNothing(isOpen, returnChildrenWhenClosed, children)}
    </div>
  );
};
type CollapsibleContentSpanProps = SpanProps &
  CollapsibleProps<HTMLSpanElement> & { modifier?: Modifier<CollapsibleModifier>; ref?: React.Ref<HTMLSpanElement> };
/**
 * Base CollapsibleContent component capable of being rendered as `htmlTag`
 */
export const CollapsibleContentSpan = ({
  children,
  isOpen,
  returnChildrenWhenClosed = false,
  className,
  modifier,
  ref,
  ...props
}: PropsWithChildren<CollapsibleContentSpanProps>) => {
  return (
    <span
      {...props}
      className={appendClassNames(className, extendClassName('collapsible-overlay', modifier))}
      ref={ref}
    >
      {returnChildrenOrNothing(isOpen, returnChildrenWhenClosed, children)}
    </span>
  );
};

/**
 * Base CollapsibleContent component capable of being rendered as `htmlTag`
 */
export function CollapsibleContentPortal({
  children,
  isOpen,
  returnChildrenWhenClosed = false,
  className,
  modifier,
  ref,
  ...props
}: PropsWithChildren<CollapsibleContentProps>) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  const element = (
    <div
      {...props}
      className={appendClassNames(className, extendClassName('collapsible-overlay', modifier))}
      ref={ref}
    >
      {returnChildrenOrNothing(isOpen, returnChildrenWhenClosed, children)}
    </div>
  );

  return mounted ? <Portal>{element}</Portal> : element;
}
